import { t } from '@avalara/bat-apps-shared/i18n';
import { SBreadcrumbs, SCol, SContainer, SIcon, SRow } from '@avalara/skylab-sdk/react';
import { useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { appendQueryParamToUrl } from '../../helpers/redirect';
import '../product-catalog.css';
import AdminAccessDialog from './admin-access-dialog';
import { trackCtaClick, useIsBlockedFromPurchasing, usePricingCtaClick } from './use-pricing-cta';

import type { TranslationKey } from '@avalara/bat-apps-shared/i18n';
import conf from '../../../conf';

import type { ProductCatalogItem } from '../../../../../types/product-catalog';
import type { ProductBreadcrumbNavItem, ProductHeroPricing } from '../../../../../types/product-detail';

type Props = {
  item: ProductCatalogItem;
  breadcrumbLabel?: string;
  breadcrumbNavItems?: Array<ProductBreadcrumbNavItem>;
  heroTitle?: string;
  heroImageUrl?: string;
  heroPricing?: ProductHeroPricing;
  hasPricing: boolean;
  // Swaps the "See pricing below" link for static "Contact account manager" text (no action --
  // BRGUI-2737) for a viewer ineligible for online purchase. Undefined/true renders unchanged.
  isEligibleForOnlinePurchase?: boolean;
  onContactClick: () => void;
};

// Rejects '.' and '..' segments (and empty segments from a leading/trailing/doubled '/') so
// heroImageUrl can never climb out of, or otherwise resolve outside, the product-detail asset
// directory — while still allowing a legitimate nested relative path like 'campaigns/Q3 Launch.png'.
const isSafeAssetPath = (path: string) =>
  path.split('/').every((segment) => segment !== '' && segment !== '.' && segment !== '..');

// Encodes each path segment individually so filenames with spaces/reserved characters are
// escaped without also escaping '/' into '%2F' and breaking an intended relative subdirectory
// (e.g. heroImageUrl values like 'campaigns/Q3 Launch.png').
const encodeAssetPath = (path: string) => path.split('/').map(encodeURIComponent).join('/');

const ProductDetailHero = ({
  item,
  breadcrumbLabel,
  breadcrumbNavItems,
  heroTitle,
  heroImageUrl,
  heroPricing,
  hasPricing,
  isEligibleForOnlinePurchase = true,
  onContactClick,
}: Props) => {
  const location = useLocation();
  const history = useHistory();
  // A hero primaryCta with ctaAction 'buy' and an offerFlow renders as the <Link> below instead of
  // going through handleCtaClick at all, so the BRGUI-2696 order-in-progress check (which only
  // runs inside handleCtaClick's 'buy' branch) never applies to it -- a known, accepted gap
  // (see docs/plans/tech-debt-tracker.md) for this pass, since no product today uses both
  // heroPricing and has an in-flight-order concern. handleCtaClick itself is still reachable for
  // 'contact' ctas (the secondary CTA, always a button), where onOrderInProgress is simply unused.
  const handleCtaClick = usePricingCtaClick(onContactClick, () => {});
  const isBlockedFromPurchasing = useIsBlockedFromPurchasing();
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const primaryCta = heroPricing?.primaryCta;
  const secondaryCta = heroPricing?.secondaryCta;
  const hasHeroImage = !!heroImageUrl && isSafeAssetPath(heroImageUrl);
  const handleBreadcrumbNav = (_event: Event, targetHref: string) => history.push(targetHref);

  return (
    <div className='product-detail-hero-banner pad-start-xl pad-end-xl'>
      <AdminAccessDialog open={adminDialogOpen} onDismiss={() => setAdminDialogOpen(false)} />
      <div className='product-detail-hero-bg-layer' aria-hidden='true' />
      <SContainer data-ref='buy.product-detail.hero' class='pad-all-none'>
        {breadcrumbNavItems?.length ? (
          <SBreadcrumbs
            navItems={breadcrumbNavItems.map(({ labelKey, url }) => ({
              label: t(labelKey as TranslationKey),
              url: url ? appendQueryParamToUrl(url, location.search.slice(1)) : url,
              emitNavEvent: !!url,
            }))}
            navHandler={handleBreadcrumbNav}
            data-ref='buy.product-detail.hero.breadcrumb'
          />
        ) : (
          <SBreadcrumbs
            navItems={[{ label: breadcrumbLabel ? t(breadcrumbLabel as TranslationKey) : item.title }]}
            data-ref='buy.product-detail.hero.breadcrumb'
          />
        )}
        <SRow>
          <SCol className='pad-all-none hero-section-descriptive-content' span={hasHeroImage ? '7' : '12'}>
            <h1 className='pad-top-sm pad-bottom-md' data-ref='buy.product-detail.hero.title'>
              {heroTitle ? t(heroTitle as TranslationKey) : item.title}
            </h1>
            <p data-ref='buy.product-detail.hero.description' className='pad-bottom-sm'>
              {t(item.description as TranslationKey)}
            </p>
            {hasPricing &&
              (isEligibleForOnlinePurchase ? (
                <a
                  className='product-detail-hero-pricing-link'
                  href='#buy-product-detail-pricing'
                  data-ref='buy.product-detail.hero.see-pricing-link'
                >
                  {t('client.product-detail.see-pricing-below')}
                </a>
              ) : (
                // No action on this copy (BRGUI-2737) -- plain text, not a link, since there's
                // nowhere on the page for it to point to once pricing is hidden.
                <span
                  className='product-detail-hero-pricing-link'
                  data-ref='buy.product-detail.hero.contact-account-manager'
                >
                  {t('client.product-detail.contact-account-manager')}
                </span>
              ))}
            {heroPricing && (
              <div className='product-detail-hero-pricing' data-ref='buy.product-detail.hero.pricing'>
                {/* price/bodyText are purchase-related (bodyText here is post-purchase onboarding
                    copy), so they hide for an ineligible viewer the same way pricing-tiers.tsx's
                    price/bodyText do (BRGUI-2737); checklistItems below stay visible either way. */}
                {heroPricing.price && isEligibleForOnlinePurchase && (
                  <p className='font-bold' data-ref='buy.product-detail.hero.pricing.price'>
                    <span className='text-md'>{heroPricing.price}</span>
                    {heroPricing.priceSubtext && <span className='text-md'> {heroPricing.priceSubtext}</span>}
                  </p>
                )}
                {heroPricing.bodyText && isEligibleForOnlinePurchase && (
                  <p className='pad-top-sm pad-bottom-sm' data-ref='buy.product-detail.hero.pricing.body-text'>
                    {t(heroPricing.bodyText as TranslationKey)}
                  </p>
                )}
                {heroPricing.checklistItems && heroPricing.checklistItems.length > 0 && (
                  <ul
                    className='product-detail-hero-pricing-checklist pad-bottom-lg'
                    data-ref='buy.product-detail.hero.pricing.checklist'
                  >
                    {heroPricing.checklistItems.map((checklistItem, index) => (
                      <li key={checklistItem} data-ref={`buy.product-detail.hero.pricing.checklist.${index}`}>
                        <SIcon name='check' aria-hidden='true' className='pad-right-xs' />
                        {t(checklistItem as TranslationKey)}
                      </li>
                    ))}
                  </ul>
                )}
                {isEligibleForOnlinePurchase ? (
                  <div
                    className='flex product-detail-hero-pricing-ctas'
                    data-ref='buy.product-detail.hero.pricing.ctas'
                  >
                    {primaryCta &&
                      (primaryCta.ctaAction === 'buy' && primaryCta.offerFlow && isBlockedFromPurchasing ? (
                        // A plain button, not a Link, for a blocked user — a Link's href stays
                        // navigable via the browser's own context menu ("open in new tab") or a
                        // middle-click, neither of which invokes this onClick, so preventDefault()
                        // here couldn't reliably stop that navigation.
                        <button
                          type='button'
                          className='button primary'
                          data-ref='buy.product-detail.hero.pricing.primary-cta'
                          onClick={() => setAdminDialogOpen(true)}
                        >
                          {t(primaryCta.ctaText as TranslationKey)}
                        </button>
                      ) : primaryCta.ctaAction === 'buy' && primaryCta.offerFlow ? (
                        // A real link (not a client-side-only button) since this genuinely navigates
                        // to another route — matches skylab's own a.button.primary styling contract.
                        // Forwards this page's own query string (e.g. ?shouldShowProductCatalog=true,
                        // a non-prod override for the catalog's gating flag) the same way
                        // catalog-card.tsx does for its own navigation — without it, Order Review's
                        // breadcrumb has nothing left to forward back to a renderable PDP (BRGUI-2570).
                        <Link
                          to={`/${primaryCta.offerFlow}${location.search}`}
                          className='button primary'
                          data-ref='buy.product-detail.hero.pricing.primary-cta'
                          onClick={() => trackCtaClick('hero-primary')}
                        >
                          {t(primaryCta.ctaText as TranslationKey)}
                        </Link>
                      ) : (
                        <button
                          type='button'
                          className='primary'
                          data-ref='buy.product-detail.hero.pricing.primary-cta'
                          onClick={() => handleCtaClick('hero-primary', primaryCta)}
                        >
                          {t(primaryCta.ctaText as TranslationKey)}
                        </button>
                      ))}
                    {secondaryCta && (
                      <button
                        type='button'
                        className='secondary'
                        data-ref='buy.product-detail.hero.pricing.secondary-cta'
                        onClick={() => handleCtaClick('hero-secondary', secondaryCta)}
                      >
                        {t(secondaryCta.ctaText as TranslationKey)}
                      </button>
                    )}
                  </div>
                ) : (
                  // No action on this copy (BRGUI-2737), same as the hasPricing swap above -- there's
                  // nowhere for it to point to once the CTAs it would otherwise sit next to are hidden.
                  <span
                    className='product-detail-hero-pricing-link'
                    data-ref='buy.product-detail.hero.pricing.contact-account-manager'
                  >
                    {t('client.product-detail.contact-account-manager')}
                  </span>
                )}
              </div>
            )}
          </SCol>
          {hasHeroImage && heroImageUrl && (
            <SCol span='5' className='flex align-items-center justify-content-center'>
              <img
                className='product-detail-hero-image'
                src={`${conf.ASSET_PATH}/assets/images/product-detail/${encodeAssetPath(heroImageUrl)}`}
                alt=''
                data-ref='buy.product-detail.hero.image'
              />
            </SCol>
          )}
        </SRow>
      </SContainer>
    </div>
  );
};

export default ProductDetailHero;
